import { Component } from 'preact'
import { render } from 'preact-render-to-string'

import {
  assignIdeaStatus,
  getPrivateId,
  getTopic,
  mergeVotes,
  replyToTopic
} from '../services/topicService'
import { ContentType } from '@insided/shared/utils/contentType'
import { IdeaStatus, Topic } from '../interfaces'
import { fetchPhrases } from './utils'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../components/tracker/publish'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@insided/deprecated-preact-components/modal'
import InputGroup from '@insided/deprecated-control/ui-components/InputGroup'
import ButtonControl from '@insided/deprecated-preact-components/button-control'
import DropdownControl from '@insided/deprecated-preact-components/dropdown/DropdownControl'
import globals from '@insided/deprecated-utils/globals'
import urlGenerator from '@insided/deprecated-preact-components/utils/urlGenerator'
import FromReply from '@insided/deprecated-control/ideas/auto-replies/FromReply'
import ToReply from '@insided/deprecated-control/ideas/auto-replies/ToReply'

interface Props {
  fromIdea: Topic
  fromIdeaStatus: IdeaStatus
  contentType: ContentType
  ideaStatuses: IdeaStatus[]
  isOpen: boolean
  onClose: () => void
  onShowAlert: (type: 'success' | 'error', message?: string) => void
}

interface State {
  toIdea: Topic | null
  toIdeaUrl: string
  nextFromIdeaStatus: IdeaStatus | null
  acknowledgement: boolean
  errors: {
    [error: string]: string
  }
  isSubmitting: boolean
}

class MergeIdeaModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      toIdea: null,
      toIdeaUrl: '',
      nextFromIdeaStatus: null,
      acknowledgement: false,
      errors: {
        toIdeaUrl: '',
        acknowledgement: ''
      },
      isSubmitting: false
    }
    this.onMergeIdea = this.onMergeIdea.bind(this)
  }

  componentDidMount = async () => {
    await fetchPhrases([
      { module: 'Common', key: 'reply.idea.status.changed' },
      { module: 'Control', key: 'idea.merge.from' },
      { module: 'Control', key: 'idea.merge.from.votes' },
      { module: 'Control', key: 'idea.merge.to' },
      { module: 'Control', key: 'idea.merge.to.votes' }
    ])
  }

  validate = () => {
    const errors = {}
    const { toIdeaUrl, acknowledgement, nextFromIdeaStatus } = this.state

    if (!toIdeaUrl) {
      Object.assign(errors, { toIdeaUrl: 'Please add a topic URL' })
    }
    if (!acknowledgement) {
      Object.assign(errors, {
        acknowledgement: 'Please confirm that you understand the ramifications of this action'
      })
    }
    if (!nextFromIdeaStatus) {
      Object.assign(errors, { nextFromIdeaStatus: 'Please select an ideation status' })
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ isSubmitting: false })
      this.setState({ errors })
      throw new Error()
    }
  }

  onMergeIdea = async () => {
    const { contentType, fromIdea, onShowAlert } = this.props
    const { toIdea, nextFromIdeaStatus } = this.state

    try {
      this.setState({ isSubmitting: true })
      this.validate()

      await mergeVotes(contentType, fromIdea.id, toIdea?.id as number)

      tracker.trackEvent('userevent', {
        type: 'Idea votes merged',
        ideaId: fromIdea.id,
        toIdeaId: toIdea?.id
      })

      await assignIdeaStatus(contentType, fromIdea.id, nextFromIdeaStatus?.id as string)

      tracker.trackEvent('userevent', {
        type: 'Idea status assigned',
        ideaId: fromIdea.id,
        ideaStatusId: nextFromIdeaStatus?.id
      })

      await this.sendAutoReplies()

      onShowAlert('success', 'The idea has been merged successfully')
      window.location.reload()
    } catch (e) {}
  }

  sendAutoReplies = async () => {
    const { fromIdeaStatus, fromIdea, contentType } = this.props
    const { toIdea, nextFromIdeaStatus } = this.state

    const fromReply = render(
      <FromReply
        oldIdeationStatus={fromIdeaStatus}
        newIdeationStatus={nextFromIdeaStatus as IdeaStatus}
        toIdeaUrl={this.ideaUrl(toIdea as Topic)}
      />
    )
    const toReply = render(<ToReply fromIdeaUrl={this.ideaUrl(fromIdea)} />)

    await replyToTopic(contentType, fromIdea.id, fromReply)
    await replyToTopic(contentType, toIdea?.id as number, toReply)
  }

  ideaUrl = (idea: Topic) => {
    return `${globals('communityUrl')}${urlGenerator('ideas', idea.title, idea.publicId)}`
  }

  onUrlInput = async (e: ChangeEvent<HTMLInputElement>) => {
    const { contentType } = this.props
    const toIdeaUrl = e.target.value

    try {
      const url = new URL(toIdeaUrl)
      const splittedPath = url.pathname.split('/')
      const isIdea = splittedPath[1] === 'ideas'

      if (!isIdea) throw new SyntaxError('Please enter an idea URL')

      const splittedTitle = splittedPath[2].split('-')
      const toIdeaPublicId = parseInt(splittedTitle[splittedTitle.length - 1])

      const privateId = await getPrivateId(toIdeaPublicId)
      const topic = await getTopic(contentType, privateId)

      this.setState(({ errors }) => ({
        toIdea: Object.assign(topic, { publicId: toIdeaPublicId }),
        toIdeaUrl: url.toString(),
        errors: { ...errors, ...{ toIdeaUrl: '' } }
      }))
    } catch (e) {
      const error: Error = e
      const errorMessage = error instanceof SyntaxError ? error.message : 'Please enter a valid URL'
      this.setState(({ errors }) => ({
        toIdeaUrl: '',
        errors: { ...errors, ...{ toIdeaUrl: errorMessage } }
      }))
    }
  }

  render() {
    const { isOpen, ideaStatuses, onClose } = this.props
    const { toIdeaUrl, acknowledgement, errors, isSubmitting, nextFromIdeaStatus } = this.state
    return (
      <Modal isOpen={isOpen} size='medium'>
        <ModalHeader title='Merge idea' onClose={() => onClose()} />
        <ModalBody>
          <div className='merge-idea-modal'>
            <InputGroup
              value={toIdeaUrl}
              title='Merge current idea into'
              type='text'
              placeholder='Enter a topic URL'
              onChange={this.onUrlInput}
              error={errors.toIdeaUrl || undefined}
            />
            <InputGroup
              title='Ideation status after merging'
              error={errors.nextFromIdeaStatus || undefined}
              customInput={
                <DropdownControl
                  closeOnDropdownClick
                  align='adjust'
                  triggerClass='dropdown-wrapper'
                  trigger={
                    <ButtonControl
                      icon='arrowDropdown'
                      title={
                        nextFromIdeaStatus
                          ? nextFromIdeaStatus.name
                          : 'Select an ideation status'
                      }
                    />
                  }
                  items={ideaStatuses
                    .filter((ideaStatus: IdeaStatus) => ideaStatus.visible)
                    .map((ideaStatus: IdeaStatus) => ({
                      title: ideaStatus.name,
                      value: ideaStatus.id,
                      onClick: () => {
                        this.setState({ nextFromIdeaStatus: ideaStatus })
                      }
                    }))}
                />
              }
            />
            <InputGroup
              inline
              title='By merging this idea, votes in the current idea will be transferred to the other idea. This cannot be undone.'
              type='checkbox'
              onChangeCheckbox={(event) => {
                return this.setState(({ errors }) => ({
                  acknowledgement: event.target.checked,
                  errors: {
                    ...errors,
                    acknowledgement: event.target.checked
                      ? ''
                      : 'Please confirm that you understand the ramifications of this action'
                  }
                }))
              }}
              checked={acknowledgement}
              error={errors.acknowledgement || undefined}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonControl
            disabled={isSubmitting}
            isLoading={isSubmitting}
            title='Merge'
            variant='primary'
            onClick={this.onMergeIdea}
            // eslint-disable-next-line @gorazdo/preact/prefer-classname
            class='qa-merge-idea'
          />
          <ButtonControl
            disabled={isSubmitting}
            title='Cancel'
            onClick={() => onClose()}
          />
        </ModalFooter>
      </Modal>
    )
  }
}

export default MergeIdeaModal
